import { MdArrowDownward, MdArrowUpward, MdInfoOutline } from 'react-icons/md'

import type { IHeaderParams } from 'ag-grid-enterprise'
import type { ReactElement } from 'react'
import type { IASOAdvanced } from '~/types/aso'

import Icon from '~/components/Icon'
import MixIcon from '~/components/MixIcon'
import Tooltip from '~/components/Tooltip'

const CustomHeader = ({
  column,
  displayName,
  enableSorting,
  progressSort,
}: IHeaderParams<IASOAdvanced>): ReactElement => {
  const onSortRequested = () => {
    progressSort()
  }

  const getTooltipContent = () => {
    switch (column.getColDef().field) {
      case 'keyword':
        return 'Click on a cell to copy its value'
      case 'keyword_quality':
        return 'The keyword quality score (from 0 to 5) is a metric computed for each keyword and based on the number of competitors that are ranked in top positions (top 10 - top 5). This metric indicates how suitable a keyword is to an app (higher score indicates higher relevancy).'
      case 'present_in':
        return (
          <div>
            <div className="row-section">
              <Icon color="#ff5301" name="title" size={21} />
              <div>Keyword is present in title</div>
            </div>
            <div className="row-section">
              <Icon color="#ff5301" name="subtitle" size={21} />
              <div>Keyword is present in subtitle</div>
            </div>
            <div className="row-section">
              <Icon color="#ff5301" name="keyword" size={21} />
              <div>Keyword is present in keywords</div>
            </div>
            <div className="row-section">
              <MixIcon isActive />
              <div>Keyword is present in multiple section</div>
            </div>
          </div>
        )
      case 'avg_sov':
        return 'The share of impressions our ad(s) received from the total impressions served on the same search terms or keywords, in the same countries and regions.'
      case 'avg_ttr':
        return 'Number of clicks on ad vs number of times displayed'
      case 'avg_cr':
        return 'Number of installs / Number of clicks on ad'
      case 'avg_install_rate':
        return 'Number of installs / Number of times displayed'
      case 'popularity':
        return 'The popularity score has a range from 5 to 100. A score of 100 means that the keyword is highly searched.'
      case 'increment_installs':
        return (
          <div>
            The incremental installs KPI is the <b>estimated</b> number of additional installs we can reach on this
            keyword if we go up in the ranking. This is the same logic as the competitiveness score, i.e. takes either
            the incremental installs of TOP 10, TOP 5 or the best ranked competitor according to our rank on this
            keyword.
          </div>
        )
      case 'competitiveness_score':
        return (
          <div>
            The competitiveness is a score for each keyword, between 0 and 5, which indicates our chances of being well
            ranked on the keyword.
            <br />
            For the sake of readability and ease of use, we have made it a single score, but this is actually the result
            of three different scores: a score that indicates our chances of reaching the <b>TOP 10</b>, a score that
            indicates our chances of reaching the <b>TOP 5</b> and a score that indicates our chances of{' '}
            <b>beating the best ranked competitor</b> on this keyword. In fine, the competitiveness score displays:
            <br />- The competitiveness score of the TOP 10 if we are currently ranked further than the TOP 10 and one
            of our competitors is in the top 10
            <br />- The competitiveness score of the TOP 5 if we are currently ranked between the sixth and tenth rank
            and if one of our competitors is in the top 5
            <br />- The competitiveness score of the best competitor if we are currently ranked between the second and
            fifth rank and if one of our competitors is better ranked than us.
          </div>
        )
      default:
        return ''
    }
  }

  return (
    <div className="advanced-aso-custom-header" onClick={onSortRequested}>
      <div>{displayName}</div>
      <Tooltip
        align={{ points: ['bl', 'tl'] }}
        mouseEnterDelay={0.2}
        overlay={<div className="advanced-aso-header-tooltip">{getTooltipContent()}</div>}
        placement="topRight"
        trigger={['hover']}
      >
        <div>
          <MdInfoOutline className="advanced-aso-header-tooltip-icon" color="#aeaeae" size={13} />
        </div>
      </Tooltip>
      {enableSorting ? (
        <>
          {column.getSort() === 'asc' ? <MdArrowDownward color="#1d1d1d" size={16} /> : null}
          {column.getSort() === 'desc' ? <MdArrowUpward color="#1d1d1d" size={16} /> : null}
        </>
      ) : null}
    </div>
  )
}

export default CustomHeader
